import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useResponsive } from '../../hooks/useResponsive';
import { VariableSizeList } from 'react-window';
import OptimizedMemberCard from './OptimizedMemberCard';
import LoadingSpinner from '../common/LoadingSpinner';
import { useVirtualizedList, useOptimizedHover, useOptimizedFavorites } from '../../hooks/useVirtualizedList';

const DEFAULT_CARD_HEIGHT = 400; // Default height estimate for cards (increased for wider screens)
const DEFAULT_COMPACT_HEIGHT = 80; // Default height estimate for compact view
const OVERSCAN_COUNT = 5; // Number of items to render outside visible area

function VirtualizedMemberList({ 
    members, 
    loading, 
    config, 
    onMemberHover, 
    onMemberHoverOut, 
    registerMemberList,
    isFavorite,
    onToggleFavorite,
    isPendingFavorite,
    isLoggedIn = true,
    showResultCount = false,
    totalMembers
}) {
    const containerRef = useRef(null);
    const itemHeights = useRef(new Map());
    const [highlightedMemberId, setHighlightedMemberId] = useState(null);
    const { isMobile, isTablet, isDesktop } = useResponsive();

    // Determine columns based on viewport; target 3 columns on desktop
    const columns = useMemo(() => {
        if (isDesktop) return 3;
        if (isTablet) return 2;
        return 1;
    }, [isDesktop, isTablet]);

    // Use our optimization hooks
    const {
        sortBy,
        viewMode,
        sortedMembers,
        listRef,
        setSortBy,
        setViewMode,
        scrollToMember
    } = useVirtualizedList(members);

    const {
        handleMemberHover,
        handleMemberHoverOut
    } = useOptimizedHover(onMemberHover, onMemberHoverOut, 150);

    const {
        checkIsFavorite,
        checkIsPending,
        handleToggleFavorite
    } = useOptimizedFavorites(isFavorite, isPendingFavorite, onToggleFavorite);

    // Register the list container with the parent
    useEffect(() => {
        if (registerMemberList && containerRef.current) {
            registerMemberList(containerRef.current);
        }
    }, [registerMemberList]);

    // Get item height for variable size list
    const getItemHeight = useCallback((index) => {
        const cached = itemHeights.current.get(index);
        if (cached) return cached;
        
        // Return estimated height based on view mode
        if (viewMode === 'compact') {
            return DEFAULT_COMPACT_HEIGHT;
        } else {
            // For cards view, we need to account for multiple cards per row
            // Use a larger estimate to prevent content cutoff at wider screens
            return DEFAULT_CARD_HEIGHT + 60; // Extra padding for row container and larger content
        }
    }, [viewMode]);

    // Set item height after render and notify react-window of changes
    const setItemHeight = useCallback((index, height) => {
        const currentHeight = itemHeights.current.get(index);
        if (currentHeight !== height) {
            itemHeights.current.set(index, height);
            // Notify react-window that heights have changed
            if (listRef.current && typeof listRef.current.resetAfterIndex === 'function') {
                listRef.current.resetAfterIndex(index, false);
            }
        }
    }, []);

    // Handle member card click for highlighting
    const handleMemberClick = useCallback((member) => {
        setHighlightedMemberId(member.user_id);
        // Optional: also trigger the hover callback for map sync
        handleMemberHover(member);
    }, [handleMemberHover]);

    // Row renderer for virtualized list
    const Row = useCallback(({ index, style }) => {
        if (viewMode === 'compact') {
            const member = sortedMembers[index];
            if (!member) return null;
            const isHighlighted = highlightedMemberId === member.user_id;
            
            return (
                <MeasuredCard
                    index={index}
                    member={member}
                    viewMode={viewMode}
                    isHighlighted={isHighlighted}
                    style={style}
                />
            );
        }

        // Cards view: render a grid row with N columns
        const startIndex = index * columns;
        const items = [];
        for (let col = 0; col < columns; col++) {
            const itemIndex = startIndex + col;
            const member = sortedMembers[itemIndex];
            if (!member) break;
            const isHighlighted = highlightedMemberId === member.user_id;
            items.push(
                <div
                    key={member.user_id}
                    className={`virtualized-member-item cards-view ${isHighlighted ? 'member-highlighted' : ''}`}
                    data-member-id={member.user_id}
                >
                    <OptimizedMemberCard
                        member={member}
                        viewMode={'cards'}
                        config={config}
                        onClick={() => handleMemberClick(member)}
                        isFavorite={checkIsFavorite}
                        onToggleFavorite={handleToggleFavorite}
                        isPendingFavorite={checkIsPending}
                        isLoggedIn={isLoggedIn}
                        data-member-id={member.user_id}
                    />
                </div>
            );
        }

        return (
            <MeasuredGridRow
                index={index}
                items={items}
                style={style}
            />
        );
    }, [sortedMembers, viewMode, config, checkIsFavorite, handleToggleFavorite, checkIsPending, highlightedMemberId, columns, handleMemberClick, isLoggedIn]);

    // Component to measure and report card height
    const MeasuredCard = useCallback(({ index, member, viewMode, isHighlighted, style }) => {
        const measureRef = useRef(null);
        
        useEffect(() => {
            if (measureRef.current) {
                const height = measureRef.current.offsetHeight;
                setItemHeight(index, height);
            }
        });

        return (
            <div ref={measureRef} style={style}>
                <div
                    className={`virtualized-member-item ${viewMode}-view ${isHighlighted ? 'member-highlighted' : ''}`}
                    data-member-id={member.user_id}
                >
                    <OptimizedMemberCard
                        key={member.user_id}
                        member={member}
                        viewMode={viewMode}
                        config={config}
                        onClick={() => handleMemberClick(member)}
                        isFavorite={checkIsFavorite}
                        onToggleFavorite={handleToggleFavorite}
                        isPendingFavorite={checkIsPending}
                        isLoggedIn={isLoggedIn}
                        data-member-id={member.user_id}
                    />
                </div>
            </div>
        );
    }, [config, handleMemberClick, checkIsFavorite, handleToggleFavorite, checkIsPending, setItemHeight, isLoggedIn]);

    // Component to measure and report grid row height
    const MeasuredGridRow = useCallback(({ index, items, style }) => {
        const measureRef = useRef(null);
        
        useEffect(() => {
            if (measureRef.current) {
                // Use requestAnimationFrame to ensure DOM has fully rendered
                requestAnimationFrame(() => {
                    if (measureRef.current) {
                        // Measure the inner grid container for accurate content height
                        const height = measureRef.current.scrollHeight;
                        setItemHeight(index, height);
                    }
                });
            }
        });

        // Extract position from style but let content determine height naturally
        const { height: _, ...positionStyle } = style;

        return (
            <div ref={measureRef} style={{...positionStyle, overflow: 'visible'}} className="virtualized-grid-row" role="row">
                <div className="virtualized-grid" style={{ display: 'grid', gridTemplateColumns: `repeat(${columns}, 1fr)`, gridAutoRows: 'minmax(auto, max-content)' }}>
                    {items}
                </div>
            </div>
        );
    }, [columns, setItemHeight]);

    // Clear cached heights when view mode changes
    useEffect(() => {
        itemHeights.current.clear();
        // Reset virtualized list to recalculate all heights
        if (listRef.current && typeof listRef.current.resetAfterIndex === 'function') {
            listRef.current.resetAfterIndex(0, true);
        }
    }, [viewMode, sortedMembers]);

    // Expose scroll method to parent
    useEffect(() => {
        if (containerRef.current) {
            containerRef.current.scrollToMember = scrollToMember;
            // Expose additional methods for map sync
            containerRef.current.highlightMember = (memberId) => {
                // Find member in sorted list and scroll to it
                const memberIndex = sortedMembers.findIndex(m => m.user_id === memberId);
                if (memberIndex !== -1 && listRef.current) {
                    if (viewMode === 'cards') {
                        // For cards view: calculate the row index since we render multiple cards per row
                        const rowIndex = Math.floor(memberIndex / columns);
                        listRef.current.scrollToItem(rowIndex, 'center');
                    } else {
                        // For compact view: use member index directly since it's one per row
                        listRef.current.scrollToItem(memberIndex, 'center');
                    }
                    // Set highlighted member ID for visual feedback
                    setHighlightedMemberId(memberId);
                    return true;
                }
                return false;
            };
            containerRef.current.clearHighlight = () => {
                setHighlightedMemberId(null);
            };
            // Flag to indicate this is a virtualized list
            containerRef.current.isVirtualized = true;
        }
    }, [scrollToMember, sortedMembers, viewMode, columns]);

    if (loading && members.length === 0) {
        return (
            <div className="member-list-container">
                <LoadingSpinner />
            </div>
        );
    }

    if (members.length === 0) {
        return (
            <div className="member-list-container">
                <div className="no-results">
                    <h3>No members found</h3>
                    <p>Try adjusting your search criteria or filters.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="member-list-container" ref={containerRef}>
            <div className="list-controls">
                <div className="sort-controls">
                    <label htmlFor="sort-select">Sort by:</label>
                    <select
                        id="sort-select"
                        value={sortBy}
                        onChange={(e) => setSortBy(e.target.value)}
                        className="sort-select"
                    >
                        <option value="name">Name (A-Z)</option>
                        <option value="name_desc">Name (Z-A)</option>
                        <option value="joined">Recently Joined</option>
                        <option value="joined_asc">Oldest Joined</option>
                    </select>
                </div>

                {showResultCount && (
                    <div className="result-count">
                        {totalMembers !== undefined ? 
                            `Displaying ${sortedMembers.length} of ${totalMembers} users found` :
                            `${sortedMembers.length} user${sortedMembers.length !== 1 ? 's' : ''} found`
                        }
                    </div>
                )}

                <div className="view-controls">
                    <button
                        onClick={() => setViewMode('cards')}
                        className={`view-btn ${viewMode === 'cards' ? 'active' : ''}`}
                        aria-label="Card view"
                    >
                        ⬜
                    </button>
                    <button
                        onClick={() => setViewMode('compact')}
                        className={`view-btn ${viewMode === 'compact' ? 'active' : ''}`}
                        aria-label="Compact view"
                    >
                        ☰
                    </button>
                </div>
            </div>

            <div className="virtualized-list-container">
                <VariableSizeList
                    ref={listRef}
                    height={500} // Will be controlled by CSS
                    itemCount={viewMode === 'cards' ? Math.ceil(sortedMembers.length / columns) : sortedMembers.length}
                    itemSize={getItemHeight}
                    overscanCount={OVERSCAN_COUNT}
                    className={`virtualized-member-list ${viewMode}-view`}
                >
                    {Row}
                </VariableSizeList>
            </div>

            {loading && (
                <div className="loading-more">
                    <LoadingSpinner size="small" />
                    Loading more members...
                </div>
            )}
        </div>
    );
}

export default VirtualizedMemberList;
